
import React from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

import { CourseForm } from "./CourseForm";
import { useAddCourse } from "./useAddCourse";
import { CourseFormData } from "./types";

export function AddCourseDialog() {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, formState: { errors } } = useForm<CourseFormData>();
  const { mutate: addCourse, isPending: isSubmitting } = useAddCourse();

  const onSubmit = async (data: CourseFormData) => {
    try {
      await addCourse(data);
      toast.success('Course added successfully');
      queryClient.invalidateQueries({ queryKey: ['courses'] });
      setOpen(false);
      reset();
    } catch (error: any) {
      console.error('Error adding course:', error);
      
      // More specific error handling
      let errorMessage = 'Failed to add course';
      
      if (error?.message) {
        // Clean up Supabase error messages for better user experience
        const message = error.message;
        if (message.includes('Permission denied')) {
          errorMessage = 'Permission denied: Only admins can add courses';
        } else if (message.includes('duplicate key')) {
          errorMessage = 'A course with this name already exists';
        } else {
          // Use the original message but clean it up
          errorMessage = message.replace(/^.*?:/, '').trim();
        }
      }
      
      toast.error(errorMessage);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Course
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Course</DialogTitle>
          <DialogDescription>Create a new course offering with the details below.</DialogDescription>
        </DialogHeader>
        <CourseForm 
          register={register} 
          errors={errors} 
          handleSubmit={handleSubmit} 
          onSubmit={onSubmit} 
          isSubmitting={isSubmitting} 
        />
      </DialogContent>
    </Dialog>
  );
}
